import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DownArrow from "../../../../../assets/pictures/icons/down-arrow-iocn.svg";
import { changeFormData, setElevenLabsVoices } from "../../../../../redux/slices";
import type { IRootState } from "../../../../../redux/store";
import type { TGender, VoiceItemTypes, VoicesResponse, VoiceTypes } from "../../../../../utils/types";
import renderDropList from "./DropList";
import style from "./NewVoice.module.scss";
import { filterVoicesByGender, mapVoicesToDropItems, normalizeVoices } from "../../../../../utils/sharedVoices";
import { useMediaPreview } from "../../../../PreviewAvatarMedia/MediaPreviewProvider";
import { useTranslation } from "react-i18next";
import { listVoices } from "../../../../../utils/api";

const NewVoice = ({ handleUserInteraction }: {handleUserInteraction: () => void}) => {
	const { t: tVoices } = useTranslation("voices");
	const { t: tForm } = useTranslation("form");
	const dispatch = useDispatch();
	const currentAvatarGender =
		useSelector((state: IRootState) => state.avatar.gender) || "male";
	const gender: TGender =
		currentAvatarGender === "male" ? tVoices("male") as TGender : tVoices("female") as TGender;
	const { setVideoState, videoRef } = useMediaPreview();
	const formData = useSelector((state: IRootState) => state.avatarForm);
	const { allVoices, elevenLabsVoices } = useSelector((state: IRootState) => state.voicesList);
	const genderVoices: VoiceTypes[] = useMemo(
		() =>
			filterVoicesByGender(allVoices, currentAvatarGender),
		[allVoices, currentAvatarGender],
	);

	const [openedDrop, setOpenedDrop] = useState<"voice" | "none">("voice");

	const fetchElevenLabsVoices = useCallback(() => {
		const cache = localStorage.getItem("elevenLabsVoices");
		if (cache) {
    		const parsed = JSON.parse(cache) as VoiceTypes[];
    		dispatch(setElevenLabsVoices(parsed));
    		return;
  		}

		return listVoices()
			.then((res) => res.json())
			.then((json) => {
				const elevenLabsVoices = normalizeVoices(json as VoicesResponse);
				localStorage.setItem("elevenLabsVoices", JSON.stringify(elevenLabsVoices));
				dispatch(setElevenLabsVoices(elevenLabsVoices));
			})
			.catch((err) => console.error(err));
	}, []);

	useEffect(() => {
		if (elevenLabsVoices.length) return;
		fetchElevenLabsVoices();
	}, []);

	const changeVoice = useCallback(
		(voice: string, ttsEngine: string) => {
			dispatch(changeFormData({ voice, ttsEngine }));
			if (!videoRef.current) return;
			setVideoState("playing");
			handleUserInteraction();
		},
		[dispatch, videoRef],
	);

	const voiceListItems: VoiceItemTypes[] = useMemo(
		() =>
			mapVoicesToDropItems(genderVoices, changeVoice),
		[genderVoices],
	);
	
	const toggleDropdown = useCallback(() => {
		setOpenedDrop((prev) => (prev === "voice" ? "none" : "voice"));
	}, []);

	return (
			<div className={style.containerVoice}>
				<h5>{tForm("choose_voice")}</h5>
				<div className={style.container}>
					<div
						onClick={toggleDropdown}
						className={`${style.containerDrop}`}
					>
						<div
							className={`${style.input} ${openedDrop === "voice" && style.active} ${
								style.selected
							}`}
						>
							<span>{gender}</span>
							<img
								src={DownArrow}
								alt="down arrow"
								className={`${style.arrow} ${openedDrop === "voice" ? style.rotate : ""}`}
							/>
						</div>
					</div>
				</div>
				{openedDrop !== "none" &&
					renderDropList({
						selected: formData.voice,
						onClick: changeVoice,
						items: voiceListItems,
				})}
			</div>
	);
};

export default NewVoice;
